import React from "react";

const URL_REGEX = /^https?:\/\/.+/;

const AllSizes = ["xs", "sm", "md", "lg", "xl", "2xl"] as const;
type Size = (typeof AllSizes)[number];

export interface AvatarProps {
  /** User's name or email to generate initials from */
  name?: string;
  /** URL of the user's profile image */
  src?: string;
  /** Size of the avatar - can be predefined sizes or custom dimensions */
  size?: Size | number | string;
  /** Custom CSS classes to apply to the avatar */
  className?: string;
  /** Whether to show a loading state */
  loading?: boolean;
  /** Fallback content when image fails to load */
  fallback?: React.ReactNode;
  /** Click handler for the avatar */
  onClick?: () => void;
  /** Whether the avatar is clickable */
  clickable?: boolean;
}

const sizeClasses = {
  xs: "w-6 h-6 text-xs",
  sm: "w-8 h-8 text-sm",
  md: "w-10 h-10 text-base",
  lg: "w-12 h-12 text-lg",
  xl: "w-16 h-16 text-xl",
  "2xl": "w-20 h-20 text-2xl"
};

export function Avatar({
  name,
  src,
  size = "md",
  className = "",
  // loading = false,
  fallback,
  onClick,
  clickable = false
}: AvatarProps) {
  // Generate initials from name
  const getInitials = (name: string): string => {
    if (!name) return "U";

    const parts = name.trim().split(/[\s@.-]+/);
    if (parts.length > 0 && parts[0]) {
      return parts[0]?.charAt(0).toUpperCase();
    } else {
      return "U";
    }
  };

  // Determine size classes
  const sizeClass =
    typeof size === "number" || (typeof size === "string" && !AllSizes.includes(size as Size))
      ? `w-${size} h-${size}`
      : sizeClasses[size as Size];

  // Determine if we should show image
  const shouldShowImage = URL_REGEX.test(src || "");

  // Base classes
  const baseClasses = `
    bg-secondary-50 text-secondary-400 font-medium
    outline-2 outline-primary-200
    rounded-full flex items-center justify-center
    ${sizeClass}
    ${clickable ? "cursor-pointer hover:opacity-80 transition-opacity" : ""}
    ${className}
  `.trim();

  // Show image if available and no error
  if (shouldShowImage) {
    return (
      <div className={baseClasses} onClick={onClick}>
        <img
          src={src}
          alt={name ? `${name}'s avatar` : "User avatar"}
          className="w-full h-full rounded-full object-cover"
        />
      </div>
    );
  }

  // Show fallback if provided
  if (fallback) {
    return (
      <div className={baseClasses} onClick={onClick}>
        {fallback}
      </div>
    );
  }

  // Show initials as default
  return (
    <div className={baseClasses} onClick={onClick}>
      <span>{getInitials(name || "")}</span>
    </div>
  );
}
